import { z } from 'zod';

/**
 * A stored card, as Stripe returns it.
 *
 * waive-dp typed `allCards` as `string[]` and then read `card.last4` off each
 * entry through an `any` — the type was simply wrong, and the display only
 * worked because nothing checked it. These are Stripe card objects, so that is
 * what the schema says.
 *
 * Field names are Stripe's (`exp_month`, not `expMonth`). Renaming them here
 * would mean the schema no longer describes the payload it parses.
 */
export const cardSchema = z.object({
  id: z.string().default(''),
  brand: z.string().default(''),
  last4: z.string().default(''),
  exp_month: z.coerce.number().default(0),
  exp_year: z.coerce.number().default(0),
});

export type Card = z.infer<typeof cardSchema>;

export const customerCardsSchema = z.object({
  defaultCard: z.string().default(''),
  allCards: z.array(cardSchema).default([]),
});

/** `GET /director/subscription`. */
export const subscriptionSchema = z.object({
  customerCards: customerCardsSchema.nullish(),
});

export type Subscription = z.infer<typeof subscriptionSchema>;

/**
 * Shared body for every charge.
 *
 * `cardId` pays with a stored card; `sourceId` pays with a fresh token from
 * Stripe Elements. A charge sends one or the other, and `saveCard` decides
 * whether a `sourceId` becomes a stored card afterwards.
 */
export interface PaymentDetails {
  cardId: string;
  sourceId: string;
  saveCard: boolean;
  paymentMethod: PaymentMethodValue;
}

/**
 * The `paymentMethod` values the API accepts.
 *
 * These are the legacy portal's own strings and the backend branches on them
 * directly, so they are not ours to tidy: `to_delete_card` means "charge this
 * one-off token, then discard it", as against `saved_card` which keeps it.
 */
export const PAYMENT_METHOD_VALUE = {
  bankTransfer: 'bank_transfer',
  savedCard: 'saved_card',
  singleUseCard: 'to_delete_card',
} as const;

export type PaymentMethodValue =
  (typeof PAYMENT_METHOD_VALUE)[keyof typeof PAYMENT_METHOD_VALUE];

/**
 * What a charge answers with.
 *
 * A BECS debit needs `client_secret` to be confirmed in the browser — but only
 * when the backend has not already settled it, which it signals by returning a
 * receipt. Both fields are optional because a card charge that succeeded
 * server-side returns neither, and the caller has to tell those cases apart
 * rather than assume.
 *
 * Field names are the API's own (`client_secret`), for the same reason the card
 * schema keeps `exp_month`: renaming here would mean the schema no longer
 * describes the payload it parses.
 */
export const paymentResultSchema = z.object({
  client_secret: z.string().nullish(),
  receipt: z
    .object({
      fileURL: z.string().nullish(),
      updatedAt: z.string().nullish(),
    })
    .nullish(),
});

export type PaymentResult = z.infer<typeof paymentResultSchema>;
